import React, { useEffect, useState, useRef } from "react"
import { useDispatch } from "react-redux"
import { Mention, MentionsInput } from 'react-mentions'
import Button from "../../components/Button/Button"
import { showSnackBar } from "../Snackbar/Snackbar"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { createNote, getMentionableUsers } from "../../features/notes/notes"
import { StorageGetItem, USER } from "../../lib/storage"


export const extractMentions = (text) => {
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g
  const mentions = []
  let match

  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[2])
  }

  return mentions
}


const NotesArea = ({
  userId,
  setNoteCreateTrigger = () => { },
  isEditMode = false,
  initialNoteText = '',
  onSave,
  saveButtonText = "Save Notes",
  exposeGetData = false
}) => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()

  const [notes, setNotes] = useState(initialNoteText)
  const [mentionUsers, setMentionUsers] = useState([])
  const [reqError, setReqError] = useState('')
  const [showSuggestionsAbove, setShowSuggestionsAbove] = useState(false)
  const containerRef = useRef(null)
  const [userRole, setUserRole] = useState('')
  const notesRef = useRef(notes)

  useEffect(() => {
    notesRef.current = notes
  }, [notes])

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  useEffect(() => {
    if (isEditMode && initialNoteText) {
      setNotes(initialNoteText)
    }
  }, [initialNoteText, isEditMode])

  const mentionStyle = {
    control: {
      backgroundColor: 'transparent',
      fontSize: 14,
      fontWeight: 'normal',
      border: '1px solid var(--color-border-divider)',
      borderRadius: '6px',
      minHeight: '100px',
    },
    '&multiLine': {
      control: {
        fontFamily: 'inherit',
        minHeight: 100,
      },
      highlighter: {
        padding: 8,
        border: '1px solid transparent',
        borderRadius: '6px',
      },
      input: {
        padding: 8,
        border: 0,
        minHeight: 100,
        outline: 0,
        resize: 'none',
        fontSize: 14,
        fontFamily: 'inherit',
        lineHeight: '1.5'
      }
    },
    suggestions: {
      list: {
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-divider)',
        width: '100%',
        overflowY: 'auto',
        zIndex: 9999,
        position: 'absolute',
        ...(showSuggestionsAbove ? {
          bottom: '100%',
          marginBottom: '4px'
        } : {
          top: '100%',
          marginTop: '4px'
        })
      },
      item: {
        padding: '8px 12px',
        borderBottom: '1px solid var(--color-border-divider)',
        cursor: 'pointer',
        '&focused': {
          backgroundColor: 'var(--color-action-hover)',
          color: 'var(--color-primary-main)'
        }
      }
    }
  }

  const handleSaveNote = async () => {
    if (isEditMode && onSave) {
      const extractedMentions = extractMentions(notes)
      await onSave(notes, extractedMentions)

    } else {
      await createNoteForUser()
    }
  }

  const getCurrentData = () => {
    return {
      text: notesRef.current,
      mentions: extractMentionsCurrentData(notesRef.current)
    }
  }

  useEffect(() => {
    if (exposeGetData && window) {
      window.__notesAreaGetCurrentData = getCurrentData
    }
    return () => {
      if (window.__notesAreaGetCurrentData) {
        delete window.__notesAreaGetCurrentData
      }
    }
  }, [exposeGetData])

  const createNoteForUser = async () => {
    triggerLoading(true)

    const extractedMentions = extractMentions(notes)

    const response = await dispatch(createNote({
      userId,
      text: notes,
      mentions: extractedMentions
    }))

    if (response.error) {
      setReqError(response.payload)
    } else {
      setNotes('')
      setNoteCreateTrigger(prev => !prev)
      showSnackBar("success", "Note successfully created")
    }
    triggerLoading(false)
  }

  const extractMentionsCurrentData = (text) => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g
    const mentions = []
    let match

    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push({
        id: match[2],
        name: match[1]
      })
    }

    return mentions
  }

  const checkSuggestionPosition = () => {
    if (!containerRef.current) return

    try {
      const inputElement = containerRef.current.querySelector('textarea') ||
        containerRef.current.querySelector('input') ||
        containerRef.current.querySelector('[role="textbox"]')

      if (!inputElement) return

      const inputRect = inputElement.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const spaceBelow = windowHeight - inputRect.bottom
      const spaceAbove = inputRect.top

      setShowSuggestionsAbove(spaceBelow < 200 && spaceAbove > 200)
    } catch (error) {
      setShowSuggestionsAbove(false)
    }
  }

  const searchUsers = (query, callback) => {
    setTimeout(checkSuggestionPosition, 0)

    if (!query) {
      callback(mentionUsers)
      return
    }

    const filteredUsers = mentionUsers.filter(user => {
      const searchText = `${user.display} ${user.department || ''}`.toLowerCase()
      return searchText.includes(query.toLowerCase())
    })

    callback(filteredUsers)
  }

  const renderSuggestion = (suggestion, search, highlightedDisplay, index, focused) => (
    <div className={`flex flex-col ${focused ? 'bg-blue-50' : ''}`}>
      <div className="font-medium text-sm">{suggestion.display}</div>
      {suggestion.department && (
        <div className="text-xs text-text-secondary">{suggestion.department}</div>
      )}
    </div>
  )

  useEffect(() => {
    const handleReqError = () => {
      if (reqError?.length > 0) {
        showSnackBar("error", reqError)
        setReqError('')
      }
    }

    handleReqError()
  }, [reqError])

  useEffect(() => {
    dispatch(getMentionableUsers()).then(res => {
      if (!res.error) {
        const users = res.payload.data?.map(u => ({
          id: u._id,
          display: u.display
        }))
        setMentionUsers(users)
      }
    })

    const handleScroll = () => {
      checkSuggestionPosition()
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  return (
    <div className="relative flex flex-col justify-between items-start text-[14px] overflow-visible w-full ">
      <div className="flex flex-col justify-center items-end w-full overflow-visible">
        {mentionUsers.length > 0 && (
          <div className="w-full mentions-container" style={{ position: 'relative' }} ref={containerRef}>
            <style jsx>{`
                .mentions-container .mentions__mention,
                .mentions-container .custom-mention,
                .mentions-container span[style],
                .mentions-container [data-cy="mention"] {
                    background-color: transparent !important;
                    color: inherit !important;
                    font-weight: inherit !important;
                    padding: 0 !important;
                    border: none !important;
                    border-radius: 0 !important;
                    box-shadow: none !important;
                    text-decoration: none !important;
                }

                .mentions-container .mentions__mention:hover,
                .mentions-container .custom-mention:hover,
                .mentions-container span[style]:hover {
                    background-color: transparent !important;
                }

                .mentions-container .mentions__suggestions {
                    position: absolute !important;
                    left: 0 !important;
                    right: 0 !important;
                    ${showSuggestionsAbove ? `
                  bottom: 100% !important;
                  top: auto !important;
                  margin-bottom: 4px !important;
                  margin-top: 0 !important;
                ` : `
                  top: 100% !important;
                  bottom: auto !important;
                  margin-top: 4px !important;
                  margin-bottom: 0 !important;
                `}
                }
            `}</style>
            <MentionsInput
              value={notes}
              onChange={(event, newValue) => {
                setNotes(newValue)
              }}
              style={mentionStyle}
              placeholder={isEditMode ? "Edit your note... Use @ to tag users" : "Add a note... Use @ to tag users"}
              a11ySuggestionsListLabel="List of users for tagging"
              allowSpaceInQuery={true}
              onFocus={checkSuggestionPosition}
              className="dark:text-text-primary"
            >
              <Mention
                trigger="@"
                data={searchUsers}
                renderSuggestion={renderSuggestion}
                style={{
                  backgroundColor: 'transparent',
                  color: 'inherit',
                  fontWeight: 'inherit',
                  padding: '0',
                  border: 'none',
                  borderRadius: '0',
                  boxShadow: 'none',
                  textDecoration: 'none'
                }}
                appendSpaceOnAdd={true}
                displayTransform={(id, display) => `@${display}`}
                markup="@[__display__](__id__)"
              />
            </MentionsInput>
          </div>
        )}
        {userId && notes.trim() && (
          <Button onClick={handleSaveNote} type="text" className="mt-2">
            {saveButtonText}
          </Button>
        )}
      </div>
    </div>
  )
}

export default NotesArea